import { useMemo } from 'react';

/**
 * F1 — Shallow-compares the booking-rules draft vs the saved baseline.
 *
 * Returns `{ isDirty }`. Used to drive the "Unsaved" pill in
 * BookingRulesHeader and to gate Save buttons.
 *
 * Normalization rules:
 *   - `null`, `undefined`, and `''` are all treated as "empty" and compare
 *     equal.
 *   - `reminderOffsets` is a CSV string ("1d,2h,30m"); order is not
 *     significant, so we split into a Set before comparing.
 *   - Numeric fields are coerced via Number() so "12" === 12.
 */
const TRACKED_SCALAR_FIELDS = [
  'bookingEnabled',
  'maxServicesAllowed',
  'multiServiceBookingsEnabled',
  'timezone',
  'rescheduleWindowHours',
  'cancellationWindowHours',
  'weekdayOpen',
  'weekdayClose',
  'weekendOpen',
  'weekendClose',
];

const isEmpty = (value) => value === null || value === undefined || value === '';

const normalizeReminderOffsets = (value) => {
  const parts = String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
  parts.sort();
  return parts.join(',');
};

const isScalarEqual = (a, b) => {
  if (isEmpty(a) && isEmpty(b)) return true;
  if (typeof a === 'number' || typeof b === 'number') {
    const na = isEmpty(a) ? NaN : Number(a);
    const nb = isEmpty(b) ? NaN : Number(b);
    if (Number.isNaN(na) && Number.isNaN(nb)) return true;
    return na === nb;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a) === String(b);
};

export const computeIsDirty = (draft, baseline) => {
  if (!draft || !baseline) return false;

  for (const field of TRACKED_SCALAR_FIELDS) {
    if (!isScalarEqual(draft[field], baseline[field])) return true;
  }

  const draftReminders = normalizeReminderOffsets(draft.reminderOffsets);
  const baselineReminders = normalizeReminderOffsets(baseline.reminderOffsets);
  if (draftReminders !== baselineReminders) return true;

  return false;
};

const useDirtySettings = (draft, baseline) => {
  const isDirty = useMemo(() => computeIsDirty(draft, baseline), [draft, baseline]);
  return { isDirty };
};

export default useDirtySettings;
